import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserLearningPaths } from '../services/firebase';
import { getLevel } from '../utils/levelSystem';
import './Dashboard.css';

function Dashboard() {
    const { currentUser, userData } = useAuth();
    const [learningPaths, setLearningPaths] = useState([]);
    const [dailyGoal, setDailyGoal] = useState({ target: 100, current: 0 });
    const [dueFlashcards, setDueFlashcards] = useState(0);
    const [streakHistory, setStreakHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, userData]);

    const loadDashboardData = async () => {
        if (!currentUser || !userData) {
            setLoading(false);
            return;
        }

        try {
            // Load learning paths
            const pathsResult = await getUserLearningPaths(currentUser.uid);
            if (pathsResult.success) {
                setLearningPaths(pathsResult.data);
            }

            // Calculate today's XP progress
            const todayXP = calculateTodayXP();
            setDailyGoal({ target: 100, current: todayXP });

            // Load due flashcards count
            const dueCount = calculateDueFlashcards();
            setDueFlashcards(dueCount);

            // Generate streak calendar data (last 30 days)
            const streakData = generateStreakCalendar();
            setStreakHistory(streakData);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        }
        setLoading(false);
    };

    const calculateTodayXP = () => {
        // This would be tracked in Firebase with timestamps
        // For now, return a sample value
        return 45;
    };

    const calculateDueFlashcards = () => {
        if (!currentUser) return 0;

        let totalDue = 0;
        const now = Date.now();

        // Check all flashcard decks in local storage
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(`flashcards_${currentUser.uid}_`)) {
                try {
                    const data = JSON.parse(localStorage.getItem(key));
                    if (data.cards) {
                        const dueCards = data.cards.filter(card => {
                            return !card.nextReview || new Date(card.nextReview).getTime() <= now;
                        });
                        totalDue += dueCards.length;
                    }
                } catch (e) {
                    console.error('Error parsing flashcard data:', e);
                }
            }
        });

        return totalDue;
    };

    const generateStreakCalendar = () => {
        // Generate last 25 days of data (5x5 grid fits nicely on mobile)
        const days = [];
        const today = new Date();
        const currentStreak = userData?.streak || 0;

        for (let i = 24; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);

            // Mark as active if within current streak
            const isActive = i < currentStreak;

            days.push({
                date: date,
                day: date.getDate(),
                isActive: isActive,
                isToday: i === 0
            });
        }

        return days;
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return '☀️ Good Morning';
        if (hour < 18) return '🌤️ Good Afternoon';
        return '🌙 Good Evening';
    };

    const getStreakMessage = () => {
        const streak = userData?.streak || 0;
        if (streak === 0) return "Start your streak today!";
        if (streak === 1) return "You're on fire! Keep it up!";
        if (streak < 7) return `${streak} days strong! 🔥`;
        if (streak < 30) return `Incredible ${streak} day streak! 🚀`;
        return `LEGENDARY ${streak} day streak! 👑`;
    };

    const getRecommendations = () => {
        const recommendations = [];

        // Due flashcards - highest priority
        if (dueFlashcards > 0) {
            recommendations.push({
                icon: '📇',
                title: 'Review Due Flashcards',
                description: `${dueFlashcards} card${dueFlashcards > 1 ? 's' : ''} ready for review`,
                action: () => navigate('/flashcards'),
                color: '#F59E0B'
            });
        }

        // Check for incomplete paths
        const incompletePaths = learningPaths.filter(path => {
            const completed = userData?.completedModules?.filter(
                m => m.startsWith(`${path.topic}_`)
            ).length || 0;
            return completed < (path.totalModules || 5);
        });

        if (incompletePaths.length > 0) {
            recommendations.push({
                icon: '📚',
                title: 'Continue Your Learning Path',
                description: `You have ${incompletePaths.length} path(s) in progress`,
                action: () => navigate('/learning-path', { state: { topic: incompletePaths[0].topic } }),
                color: '#667eea'
            });
        }

        // Daily goal not met
        if (dailyGoal.current < dailyGoal.target) {
            recommendations.push({
                icon: '🎯',
                title: 'Complete Your Daily Goal',
                description: `${dailyGoal.target - dailyGoal.current} XP to go!`,
                action: () => navigate('/home'),
                color: '#f093fb'
            });
        }

        // Streak at risk
        if ((userData?.streak || 0) > 0) {
            recommendations.push({
                icon: '🔥',
                title: 'Maintain Your Streak',
                description: getStreakMessage(),
                action: () => navigate('/home'),
                color: '#fa709a'
            });
        }

        // New learner
        if (learningPaths.length === 0) {
            recommendations.push({
                icon: '🚀',
                title: 'Start Your First Learning Path',
                description: 'Choose any topic and begin your journey',
                action: () => navigate('/home'),
                color: '#4facfe'
            });
        }

        return recommendations.slice(0, 3);
    };

    const calculatePathProgress = (path) => {
        const completedModules = userData?.completedModules?.filter(
            m => m.startsWith(`${path.topic}_`)
        ).length || 0;
        const totalModules = path.totalModules || 5;
        return Math.round((completedModules / totalModules) * 100);
    };

    const getUpcomingTasks = () => {
        const tasks = [];

        // Add incomplete modules
        learningPaths.forEach(path => {
            const completedModules = userData?.completedModules?.filter(
                m => m.startsWith(`${path.topic}_`)
            ) || [];

            if (completedModules.length < (path.totalModules || 5)) {
                const nextModule = completedModules.length + 1;
                tasks.push({
                    type: 'module',
                    icon: '📖',
                    title: `${path.topic} - Module ${nextModule}`,
                    topic: path.topic,
                    moduleId: nextModule
                });
            }
        });

        return tasks.slice(0, 5);
    };

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="spinner"></div>
                <p>Loading your dashboard...</p>
            </div>
        );
    }

    const recommendations = getRecommendations();
    const upcomingTasks = getUpcomingTasks();
    const goalProgress = (dailyGoal.current / dailyGoal.target) * 100;
    const levelInfo = getLevel(userData?.totalXP || 0);

    return (
        <div className="dashboard-container">
            {/* Header */}
            <div className="dashboard-header">
                <div className="greeting-section">
                    <h1 className="greeting">{getGreeting()}, {userData?.displayName || 'Learner'}!</h1>
                    <p className="greeting-subtitle">Ready to level up your knowledge?</p>
                </div>
            </div>

            {/* Continue Learning Section */}
            {learningPaths.length > 0 && (
                <div className="continue-learning-section">
                    <div className="section-header">
                        <h2>Continue Learning</h2>
                        <p>Pick up where you left off</p>
                    </div>
                    <div className="continue-learning-grid">
                        {learningPaths.slice(0, 3).map((path) => {
                            const progress = calculatePathProgress(path);
                            return (
                                <div
                                    key={path.id}
                                    className="continue-card"
                                    onClick={() => navigate('/learning-path', {
                                        state: { topic: path.topic, existingPath: path }
                                    })}
                                >
                                    <div className="continue-card-header">
                                        <div className="continue-icon">📚</div>
                                        <div className="continue-badge">{progress}% Complete</div>
                                    </div>
                                    <h3 className="continue-title">{path.topic}</h3>
                                    <div className="continue-progress">
                                        <div
                                            className="continue-progress-fill"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                    <button className="continue-btn">
                                        Continue Learning →
                                    </button>
                                </div>
                            );
                        })}
                        <div
                            className="continue-card new-path-card"
                            onClick={() => navigate('/home')}
                        >
                            <div className="new-path-icon">✨</div>
                            <h3>Start New Topic</h3>
                            <p>Explore something new</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Stats Row */}
            <div className="quick-stats-grid">
                <div className="quick-stat-card">
                    <div className="stat-icon-large">{levelInfo.icon}</div>
                    <div className="stat-content">
                        <div className="stat-value-large">{levelInfo.title}</div>
                        <div className="stat-label-small">Level {levelInfo.level} • {userData?.totalXP || 0} XP</div>
                    </div>
                </div>

                <div className="quick-stat-card">
                    <div className="stat-icon-large">🔥</div>
                    <div className="stat-content">
                        <div className="stat-value-large">{userData?.streak || 0}</div>
                        <div className="stat-label-small">Day Streak</div>
                    </div>
                </div>

                <div className="quick-stat-card">
                    <div className="stat-icon-large">📚</div>
                    <div className="stat-content">
                        <div className="stat-value-large">{learningPaths.length}</div>
                        <div className="stat-label-small">Active Paths</div>
                    </div>
                </div>

                <div className="quick-stat-card">
                    <div className="stat-icon-large">✅</div>
                    <div className="stat-content">
                        <div className="stat-value-large">{userData?.completedModules?.length || 0}</div>
                        <div className="stat-label-small">Completed</div>
                    </div>
                </div>
            </div>

            {/* Daily Goal */}
            <div className="daily-goal-section">
                <div className="section-header">
                    <h2>🎯 Today's Goal</h2>
                    <span className="goal-status">
                        {dailyGoal.current >= dailyGoal.target ? '✅ Complete!' : `${Math.round(goalProgress)}%`}
                    </span>
                </div>
                <div className="goal-progress-bar">
                    <div
                        className="goal-progress-fill"
                        style={{ width: `${Math.min(goalProgress, 100)}%` }}
                    />
                </div>
                <div className="goal-details">
                    <span>{dailyGoal.current} / {dailyGoal.target} XP</span>
                    {dailyGoal.current < dailyGoal.target && (
                        <span className="goal-remaining">
                            {dailyGoal.target - dailyGoal.current} XP to go!
                        </span>
                    )}
                </div>
            </div>

            {/* Streak Calendar */}
            <div className="streak-calendar-section">
                <h2 className="section-title">🔥 Streak Calendar</h2>
                <p className="streak-subtitle">{getStreakMessage()}</p>
                <div className="calendar-grid">
                    {streakHistory.map((day, index) => (
                        <div
                            key={index}
                            className={`calendar-day ${day.isActive ? 'active' : ''} ${day.isToday ? 'today' : ''}`}
                            title={day.date.toLocaleDateString()}
                        >
                            <div className="day-number">{day.day}</div>
                            {day.isActive && <div className="day-indicator">🔥</div>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="dashboard-grid">
                {/* Recommendations */}
                <div className="dashboard-section recommendations-section">
                    <h2 className="section-title">💡 Recommended For You</h2>
                    <div className="recommendations-list">
                        {recommendations.length > 0 ? (
                            recommendations.map((rec, index) => (
                                <div
                                    key={index}
                                    className="recommendation-card"
                                    onClick={rec.action}
                                    style={{ borderLeftColor: rec.color }}
                                >
                                    <div className="rec-icon" style={{ background: rec.color }}>
                                        {rec.icon}
                                    </div>
                                    <div className="rec-content">
                                        <h3>{rec.title}</h3>
                                        <p>{rec.description}</p>
                                    </div>
                                    <div className="rec-arrow">→</div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state-small">
                                <p>You're all caught up! 🎉</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Upcoming Tasks */}
                <div className="dashboard-section tasks-section">
                    <h2 className="section-title">📋 Up Next</h2>
                    <div className="tasks-list">
                        {upcomingTasks.length > 0 ? (
                            upcomingTasks.map((task, index) => (
                                <div
                                    key={index}
                                    className="task-item"
                                    onClick={() => navigate('/learning-path', { state: { topic: task.topic } })}
                                >
                                    <span className="task-icon">{task.icon}</span>
                                    <span className="task-title">{task.title}</span>
                                    <span className="task-action">→</span>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state-small">
                                <p>No upcoming tasks</p>
                                <button
                                    className="btn btn-primary btn-small"
                                    onClick={() => navigate('/home')}
                                >
                                    Start Learning
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Continue Learning */}
            <div className="continue-learning-section">
                <h2 className="section-title">🚀 Continue Learning</h2>
                <div className="learning-paths-grid">
                    {learningPaths.length > 0 ? (
                        learningPaths.slice(0, 3).map((path, index) => {
                            const completedModules = userData?.completedModules?.filter(
                                m => m.startsWith(`${path.topic}_`)
                            ).length || 0;
                            const totalModules = path.totalModules || 5;
                            const progress = (completedModules / totalModules) * 100;

                            return (
                                <div
                                    key={index}
                                    className="learning-path-card"
                                    onClick={() => navigate('/learning-path', { state: { topic: path.topic } })}
                                >
                                    <h3>{path.topic}</h3>
                                    <div className="path-progress">
                                        <div className="path-progress-bar">
                                            <div
                                                className="path-progress-fill"
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                        <span className="path-progress-text">
                                            {completedModules}/{totalModules} modules
                                        </span>
                                    </div>
                                    <div className="path-meta">
                                        <span>⏱️ {path.estimatedTime || 'N/A'}</span>
                                        <span className="continue-btn">Continue →</span>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="empty-state">
                            <p>No learning paths yet. Start your journey!</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => navigate('/home')}
                            >
                                🚀 Start Learning
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
